import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Package, Plus, Trash2, Edit2, Send, Check, X, AlertTriangle,
  DollarSign, FileText, Clock, Users, ChevronDown, ChevronUp, Search, BookOpen, Sparkles,
  FolderOpen, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

/**
 * JobAdditionalMaterials Component
 * Manages additional materials added to a job AFTER the original estimate was converted
 * Includes change order functionality to send additions to customer for approval
 */
export function JobAdditionalMaterials({ 
  job, 
  linkedEstimate,
  apiClient,
  onUpdate,
  isAdmin = false,
  isEmployee = false
}) {
  const [additionalMaterials, setAdditionalMaterials] = useState([]);
  const [changeOrders, setChangeOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [changeOrderDialogOpen, setChangeOrderDialogOpen] = useState(false);
  const [selectedForChangeOrder, setSelectedForChangeOrder] = useState([]);
  const [expanded, setExpanded] = useState(true);
  
  // Material catalog search state
  const [materialCatalog, setMaterialCatalog] = useState([]);
  const [catalogSearchTerm, setCatalogSearchTerm] = useState('');
  const [filteredCatalog, setFilteredCatalog] = useState([]);
  const [addMode, setAddMode] = useState('catalog'); // 'catalog', 'bundles', or 'new'
  // Bundles let the user dump a pre-built set of catalog materials into the
  // job in one click — same UX as estimates. We resolve each bundle item
  // against the in-memory catalog so coverage_rate / markup carry through.
  const [materialBundles, setMaterialBundles] = useState([]);
  const [bundleSearchTerm, setBundleSearchTerm] = useState('');
  const [addingBundleId, setAddingBundleId] = useState(null);
  
  const [newMaterial, setNewMaterial] = useState({
    material_name: '',
    quantity: 1,
    unit: 'each',
    unit_cost: 0,
    markup_percent: 0,
    notes: '',
    reason: '',
    requires_approval: false,
    status: 'pending',
    // New fields for catalog integration
    material_id: null,
    add_to_catalog: true,
    catalog_category: '',
    catalog_description: '',
    coverage_rate: null
  });

  const [newChangeOrder, setNewChangeOrder] = useState({
    title: '',
    description: '',
    additional_labor_hours: 0,
    labor_hourly_rate: 0,
    notes: ''
  });

  useEffect(() => {
    if (job?.id) {
      fetchAdditionalMaterials();
      fetchChangeOrders();
      fetchMaterialCatalog();
      fetchMaterialBundles();
    }
  }, [job?.id]);

  // Fetch material catalog for selection
  const fetchMaterialCatalog = async () => {
    try {
      const res = await apiClient.get('/material-catalog');
      setMaterialCatalog(res.data || []);
    } catch (err) {
      console.error('Failed to fetch material catalog:', err?.response?.status, err?.response?.data);
      setMaterialCatalog([]);
      // Surface failures so the empty catalog state isn't a mystery in
      // production. 403 = feature flag off; other codes = something else.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) {
        toast.error(detail || "Materials Catalog feature is not enabled for your account.");
      } else if (status) {
        toast.error(`Could not load catalog (HTTP ${status}). ${detail || ''}`);
      }
    }
  };

  // Fetch material bundles — same endpoint estimates uses. Fail-soft so
  // tenants without any bundles still see the empty-state CTA instead of
  // a spinner forever.
  const fetchMaterialBundles = async () => {
    try {
      const res = await apiClient.get('/material-bundles');
      setMaterialBundles(res.data || []);
    } catch (err) {
      console.error('Failed to fetch material bundles:', err);
      setMaterialBundles([]);
    }
  };

  // Filter catalog based on search term
  useEffect(() => {
    if (!catalogSearchTerm.trim()) {
      setFilteredCatalog(materialCatalog.slice(0, 20)); // Show first 20 when no search
    } else {
      const searchLower = catalogSearchTerm.toLowerCase();
      const filtered = materialCatalog.filter(m => 
        m.name?.toLowerCase().includes(searchLower) ||
        m.category?.toLowerCase().includes(searchLower)
      );
      setFilteredCatalog(filtered.slice(0, 20));
    }
  }, [catalogSearchTerm, materialCatalog]);

  const fetchAdditionalMaterials = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/jobs/${job.id}/additional-materials`);
      setAdditionalMaterials(res.data.materials || []);
    } catch (err) {
      console.error('Failed to fetch additional materials:', err);
      setAdditionalMaterials([]);
    }
    setLoading(false);
  };

  const fetchChangeOrders = async () => {
    try {
      const res = await apiClient.get(`/jobs/${job.id}/change-orders`);
      setChangeOrders(res.data || []);
    } catch (err) {
      console.error('Failed to fetch change orders:', err);
      setChangeOrders([]);
    }
  };

  // Select a catalog item
  const selectCatalogItem = (item) => {
    setNewMaterial({
      ...newMaterial,
      material_name: item.name,
      unit: item.unit || 'each',
      // Catalog stores `unit_cost` (not legacy `cost_per_unit`) and
      // `default_markup_percent` (not `markup_percent`). Keep fallbacks so
      // ancient rows still work if they were seeded with the legacy names.
      unit_cost: item.unit_cost ?? item.cost_per_unit ?? 0,
      markup_percent: item.default_markup_percent ?? item.markup_percent ?? 0,
      material_id: item.id,
      add_to_catalog: false, // Already in catalog
      catalog_category: item.category || '',
      catalog_description: item.description || '',
      coverage_rate: item.coverage_rate || null
    });
    setCatalogSearchTerm('');
    toast.success(`Selected: ${item.name}`);
  };

  // Add every material in a bundle to the active job — one POST per material
  // so each gets its own row + audit trail (and tenants get individual
  // approve/reject controls if `requires_approval` is on). Resolves bundle
  // items against the catalog so coverage_rate / markup metadata follow.
  const addBundleToJob = async (bundle) => {
    if (!bundle?.materials?.length) {
      toast.error('This bundle has no materials');
      return;
    }
    setAddingBundleId(bundle.id);
    let succeeded = 0;
    let failed = 0;
    for (const mat of bundle.materials) {
      const cat = materialCatalog.find(c => c.id === mat.material_id) || {};
      const payload = {
        material_name: mat.material_name || cat.name || 'Material',
        quantity: mat.default_quantity || 1,
        unit: mat.unit || cat.unit || 'each',
        // See selectCatalogItem — catalog fields are `unit_cost` +
        // `default_markup_percent`; keep the legacy names as fallbacks.
        unit_cost: mat.unit_cost ?? cat.unit_cost ?? cat.cost_per_unit ?? 0,
        markup_percent: mat.markup_percent ?? cat.default_markup_percent ?? cat.markup_percent ?? 0,
        notes: `From bundle: ${bundle.name}`,
        reason: '',
        requires_approval: false,
        status: 'approved',
        material_id: mat.material_id || null,
        add_to_catalog: false,
        catalog_category: cat.category || '',
        catalog_description: cat.description || '',
        coverage_rate: cat.coverage_rate ?? null,
      };
      try {
        await apiClient.post(`/jobs/${job.id}/additional-materials`, payload);
        succeeded += 1;
      } catch (err) {
        console.error('Bundle item add failed:', payload.material_name, err);
        failed += 1;
      }
    }
    setAddingBundleId(null);
    if (succeeded > 0) {
      await fetchAdditionalMaterials();
      if (onUpdate) onUpdate();
      toast.success(
        failed > 0
          ? `Added ${succeeded} from "${bundle.name}" (${failed} failed)`
          : `Added ${succeeded} materials from "${bundle.name}"`
      );
      setAddDialogOpen(false);
    } else {
      toast.error(`Couldn't add any materials from "${bundle.name}"`);
    }
  };

  // Reset form for new material
  const resetToNewMaterial = () => {
    setNewMaterial({
      material_name: '',
      quantity: 1,
      unit: 'each',
      unit_cost: 0,
      markup_percent: 0,
      notes: '',
      reason: '',
      requires_approval: false,
      status: 'pending',
      material_id: null,
      add_to_catalog: true,
      catalog_category: '',
      catalog_description: '',
      coverage_rate: null
    });
    setCatalogSearchTerm('');
  };

  const handleAddMaterial = async () => {
    if (!newMaterial.material_name) {
      toast.error("Please enter a material name");
      return;
    }
    
    try {
      const response = await apiClient.post(`/jobs/${job.id}/additional-materials`, newMaterial);
      if (response.data.added_to_catalog) {
        toast.success("Material added & saved to catalog!");
        fetchMaterialCatalog(); // Refresh catalog
      } else {
        toast.success("Additional material added");
      }
      setAddDialogOpen(false);
      resetToNewMaterial();
      fetchAdditionalMaterials();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add material");
    }
  };

  const handleUpdateStatus = async (materialId, newStatus) => {
    try {
      await apiClient.put(`/jobs/${job.id}/additional-materials/${materialId}`, {
        status: newStatus
      });
      toast.success(`Material ${newStatus}`);
      fetchAdditionalMaterials();
      onUpdate?.();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    try {
      await apiClient.delete(`/jobs/${job.id}/additional-materials/${materialId}`);
      toast.success("Material removed");
      fetchAdditionalMaterials();
      onUpdate?.();
    } catch (err) {
      toast.error("Failed to delete material");
    }
  };

  const handleCreateChangeOrder = async () => {
    if (!newChangeOrder.title) {
      toast.error("Please enter a change order title");
      return;
    }
    
    try {
      await apiClient.post(`/jobs/${job.id}/change-orders`, {
        ...newChangeOrder,
        additional_material_ids: selectedForChangeOrder
      });
      toast.success("Change order created");
      setChangeOrderDialogOpen(false);
      setNewChangeOrder({
        title: '',
        description: '',
        additional_labor_hours: 0,
        labor_hourly_rate: 0,
        notes: ''
      });
      setSelectedForChangeOrder([]);
      fetchChangeOrders();
      fetchAdditionalMaterials();
    } catch (err) {
      toast.error("Failed to create change order");
    }
  };

  const handleSendChangeOrder = async (orderId) => {
    try {
      await apiClient.post(`/jobs/${job.id}/change-orders/${orderId}/send`);
      toast.success("Change order sent to customer");
      fetchChangeOrders();
    } catch (err) {
      toast.error("Failed to send change order");
    }
  };

  const handleUpdateChangeOrderStatus = async (orderId, status) => {
    try {
      await apiClient.put(`/jobs/${job.id}/change-orders/${orderId}/status`, { status });
      toast.success(`Change order ${status}`);
      fetchChangeOrders();
      fetchAdditionalMaterials();
      onUpdate?.();
    } catch (err) {
      toast.error("Failed to update change order");
    }
  };

  // Calculate totals
  const totalAdditionalCost = additionalMaterials.reduce((sum, m) => sum + (m.total_with_markup || 0), 0);
  const pendingApproval = additionalMaterials.filter(m => m.status === 'pending' && m.requires_approval);
  const approvedMaterials = additionalMaterials.filter(m => m.status === 'approved' || !m.requires_approval);

  // Separate breakdown for the Job Financial Summary card so admins can see
  // three numbers at a glance:
  //   • Materials Cost  = raw out-of-pocket (no markup)
  //   • Materials Billable = what the customer would owe if these were
  //     quoted today (with each row's markup applied)
  //   • Materials Margin = Billable − Cost (profit on materials only)
  // Approved + pending are both included so admins see the full picture.
  const additionalRawCost = additionalMaterials.reduce((sum, m) => sum + (m.total_cost || (m.quantity || 0) * (m.unit_cost || 0)), 0);
  const additionalBillable = additionalMaterials.reduce((sum, m) => sum + (m.total_with_markup || m.total_cost || (m.quantity || 0) * (m.unit_cost || 0)), 0);
  const additionalMargin = additionalBillable - additionalRawCost;
  const marginPct = additionalRawCost > 0 ? (additionalMargin / additionalRawCost) * 100 : 0;

  // Get original estimate materials for comparison
  const originalMaterials = linkedEstimate?.material_items || [];
  const originalMaterialsCost = originalMaterials.reduce((sum, m) => sum + ((m.quantity || 0) * (m.unit_cost || 0)), 0);
  // Original quoted total (with markup) — pulled from the linked estimate
  // so the summary card can show "quoted vs actual" side by side.
  const linkedEstimateQuoted = linkedEstimate?.total_price ?? linkedEstimate?.grand_total ?? null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'rejected': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'completed': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <Card className="bg-zinc-800 border-zinc-700">
        <CardHeader 
          className="pb-2 cursor-pointer" 
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Package className="w-4 h-4 text-orange-400" />
              Additional Materials
              {additionalMaterials.length > 0 && (
                <Badge variant="outline" className="border-orange-500/30 text-orange-400 ml-2">
                  {additionalMaterials.length} items
                </Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              {!isEmployee && totalAdditionalCost > 0 && (
                <span className="text-orange-400 font-bold">+${totalAdditionalCost.toLocaleString()}</span>
              )}
              {expanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
            </div>
          </div>
        </CardHeader>
        
        {expanded && (
          <CardContent className="pt-2">
            {/* Job Financial Summary — three-column view so admins immediately
                see (1) what the customer agreed to pay from the estimate,
                (2) what materials cost them, (3) what those materials
                would be worth billed with markup. Only rendered when there
                are additional materials OR a linked estimate to compare
                against — new blank jobs stay uncluttered. */}
            {!isEmployee && (additionalMaterials.length > 0 || linkedEstimateQuoted !== null) && (
              <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-2 rounded-lg border border-zinc-700 bg-zinc-900/50 p-3" data-testid="job-financial-summary">
                <div className="rounded-md bg-zinc-800/60 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-zinc-500">Customer Quoted</p>
                  <p className="text-lg font-bold text-cyan-300">
                    {linkedEstimateQuoted !== null ? `$${Number(linkedEstimateQuoted).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                  </p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">From the estimate</p>
                </div>
                <div className="rounded-md bg-zinc-800/60 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-zinc-500">Materials Cost</p>
                  <p className="text-lg font-bold text-zinc-100">${additionalRawCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">What it cost you</p>
                </div>
                <div className="rounded-md bg-zinc-800/60 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-zinc-500">Materials Billable</p>
                  <p className="text-lg font-bold text-orange-400">${additionalBillable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  <p className={`text-[10px] mt-0.5 ${additionalMargin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {additionalMargin >= 0 ? '+' : ''}${additionalMargin.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {additionalRawCost > 0 && ` (${marginPct >= 0 ? '+' : ''}${marginPct.toFixed(0)}% margin)`}
                  </p>
                </div>
              </div>
            )}

            {/* Original Estimate Section - Admin Only */}
            {!isEmployee && linkedEstimate && originalMaterials.length > 0 && (
              <div className="mb-4 p-3 bg-zinc-900/50 rounded-lg border border-zinc-700">
                <p className="text-xs text-zinc-400 uppercase mb-2">Original Estimate Materials</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {originalMaterials.slice(0, 5).map((mat, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-zinc-300">{mat.description || mat.name}</span>
                      <span className="text-zinc-400">{mat.quantity} × ${mat.unit_cost?.toFixed(2)}</span>
                    </div>
                  ))}
                  {originalMaterials.length > 5 && (
                    <p className="text-xs text-zinc-500">+{originalMaterials.length - 5} more items</p>
                  )}
                </div>
                <div className="mt-2 pt-2 border-t border-zinc-700 flex justify-between">
                  <span className="text-sm text-zinc-400">Original Total</span>
                  <span className="text-sm text-white font-medium">${originalMaterialsCost.toLocaleString()}</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 mb-4">
              <Button 
                size="sm" 
                onClick={() => setAddDialogOpen(true)} 
                className="bg-orange-500 text-white hover:bg-orange-600"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Material
              </Button>
              {pendingApproval.length > 0 && isAdmin && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => {
                    setSelectedForChangeOrder(pendingApproval.map(m => m.id));
                    setNewChangeOrder({
                      ...newChangeOrder,
                      title: `Change Order - ${job.title}`
                    });
                    setChangeOrderDialogOpen(true);
                  }}
                  className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-900/30"
                >
                  <Send className="w-4 h-4 mr-1" /> Create Change Order ({pendingApproval.length})
                </Button>
              )}
            </div>

            {/* Additional Materials List */}
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : additionalMaterials.length > 0 ? (
              <div className="space-y-2">
                {additionalMaterials.map((mat) => (
                  <div 
                    key={mat.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      mat.status === 'approved' ? 'bg-green-900/10 border-green-500/30' :
                      mat.status === 'rejected' ? 'bg-red-900/10 border-red-500/30' :
                      'bg-zinc-800/50 border-zinc-700'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium">{mat.material_name}</p>
                        <Badge variant="outline" className={getStatusColor(mat.status)}>
                          {mat.status}
                        </Badge>
                        {mat.requires_approval && mat.status === 'pending' && (
                          <Badge variant="outline" className="border-yellow-500/30 text-yellow-400">
                            <AlertTriangle className="w-3 h-3 mr-1" /> Needs Approval
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-zinc-400 mt-1">
                        <span>{mat.quantity} {mat.unit}</span>
                        {!isEmployee && <span>${mat.unit_cost?.toFixed(2)}/{mat.unit}</span>}
                        {!isEmployee && mat.markup_percent > 0 && (
                          <span className="text-green-400">+{mat.markup_percent}% markup</span>
                        )}
                        {mat.reason && <span className="text-cyan-400">Reason: {mat.reason}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isEmployee && (
                        <div className="text-right mr-2">
                          {/* Show cost AND billable side-by-side so the
                              admin never has to guess which number is
                              which. Cost = out-of-pocket. Billable = what
                              a customer would owe if this were a change
                              order or retro-estimate line today. */}
                          <p className="text-xs text-zinc-500">
                            Your Cost:
                            <span className="text-zinc-200 font-medium ml-1">${(mat.total_cost || 0).toFixed(2)}</span>
                          </p>
                          <p className="text-xs mt-0.5">
                            <span className="text-zinc-500">Billable{mat.markup_percent > 0 ? ` (+${mat.markup_percent}%)` : ''}:</span>
                            <span className="text-orange-400 font-bold ml-1">${(mat.total_with_markup || mat.total_cost || 0).toFixed(2)}</span>
                          </p>
                        </div>
                      )}
                      {mat.status === 'pending' && isAdmin && (
                        <>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleUpdateStatus(mat.id, 'approved')}
                            className="text-green-400 hover:bg-green-900/30"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleUpdateStatus(mat.id, 'rejected')}
                            className="text-red-400 hover:bg-red-900/30"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {isAdmin && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleDeleteMaterial(mat.id)}
                          className="text-red-400 hover:bg-red-900/30"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-zinc-500">
                <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No additional materials added yet</p>
                <p className="text-xs">Add materials that weren't in the original estimate</p>
              </div>
            )}

            {/* Change Orders Section - Admin Only */}
            {!isEmployee && changeOrders.length > 0 && (
              <div className="mt-4 pt-4 border-t border-zinc-700">
                <p className="text-xs text-zinc-400 uppercase mb-2">Change Orders</p>
                <div className="space-y-2">
                  {changeOrders.map((order) => (
                    <div 
                      key={order.id} 
                      className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg border border-zinc-700"
                    >
                      <div>
                        <p className="text-white font-medium">{order.title}</p>
                        <div className="flex items-center gap-2 text-xs text-zinc-400">
                          <span>{order.materials?.length || 0} items</span>
                          {order.additional_labor_hours > 0 && (
                            <span>+ {order.additional_labor_hours}h labor</span>
                          )}
                          <Badge variant="outline" className={getStatusColor(order.status)}>
                            {order.status}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-primary font-bold">${order.total_amount?.toLocaleString()}</p>
                        {order.status === 'pending' && isAdmin && (
                          <Button 
                            size="sm"
                            onClick={() => handleSendChangeOrder(order.id)}
                            className="bg-cyan-500 text-white hover:bg-cyan-600"
                          >
                            <Send className="w-4 h-4 mr-1" /> Send
                          </Button>
                        )}
                        {order.status === 'sent' && isAdmin && (
                          <>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateChangeOrderStatus(order.id, 'approved')}
                              className="border-green-500/50 text-green-400"
                            >
                              <Check className="w-4 h-4" /> Approve
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateChangeOrderStatus(order.id, 'rejected')}
                              className="border-red-500/50 text-red-400"
                            >
                              <X className="w-4 h-4" /> Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Total Summary - Admin Only */}
            {!isEmployee && (totalAdditionalCost > 0 || approvedMaterials.length > 0) && (
              <div className="mt-4 pt-4 border-t border-zinc-700">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-zinc-400">Total Additional Materials</p>
                    <p className="text-xs text-zinc-500">{approvedMaterials.length} approved items</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-orange-400">+${totalAdditionalCost.toLocaleString()}</p>
                    {originalMaterialsCost > 0 && (
                      <p className="text-xs text-zinc-500">
                        Job Total: ${(originalMaterialsCost + totalAdditionalCost).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Add Material Dialog - Enhanced with Catalog Search */}
      <Dialog open={addDialogOpen} onOpenChange={(open) => {
        setAddDialogOpen(open);
        if (!open) {
          resetToNewMaterial();
          setAddMode('catalog');
        }
      }}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Package className="w-5 h-5 text-orange-400" /> Add Additional Material
            </DialogTitle>
          </DialogHeader>

          {/* Mental-model hint — customers often confuse "material on job"
              with "material on estimate". Job materials are for COST
              tracking; the customer's price is locked at the estimate.
              To bill more, use a Change Order (button surfaces in the list
              header once a pending row exists). */}
          <div className="rounded-md border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-100 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-cyan-400" />
            <div>
              Materials logged here track your <span className="font-semibold">cost</span>. The customer&apos;s price is set by the estimate. To bill the customer more, mark the row as <span className="text-yellow-300">Needs Approval</span> and use a <span className="font-semibold">Change Order</span>.
            </div>
          </div>

          {/* Tabs to switch between catalog and new material */}
          <Tabs value={addMode} onValueChange={(v) => {
            setAddMode(v);
            if (v === 'new') resetToNewMaterial();
          }} className="w-full">
            <TabsList className="grid w-full grid-cols-3 bg-zinc-800">
              <TabsTrigger value="catalog" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                <BookOpen className="w-4 h-4 mr-1.5" /> Catalog
              </TabsTrigger>
              <TabsTrigger
                value="bundles"
                className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white"
                data-testid="bundles-tab-trigger"
              >
                <FolderOpen className="w-4 h-4 mr-1.5" /> Bundles
                {materialBundles.length > 0 && (
                  <span className="ml-1 text-[10px] opacity-80">({materialBundles.length})</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="new" className="data-[state=active]:bg-green-500 data-[state=active]:text-white">
                <Sparkles className="w-4 h-4 mr-1.5" /> New
              </TabsTrigger>
            </TabsList>
            
            {/* Catalog Search Tab */}
            <TabsContent value="catalog" className="space-y-4 mt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  value={catalogSearchTerm}
                  onChange={(e) => setCatalogSearchTerm(e.target.value)}
                  placeholder="Search materials catalog..."
                  className="pl-10 bg-zinc-800 border-zinc-700"
                />
              </div>
              
              {/* Selected material info */}
              {newMaterial.material_id && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-400 font-medium">{newMaterial.material_name}</p>
                      <p className="text-xs text-zinc-400">
                        ${newMaterial.unit_cost}/{newMaterial.unit} • {newMaterial.markup_percent}% markup
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        resetToNewMaterial();
                        setAddMode('catalog');
                      }}
                      className="text-zinc-400 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Catalog results */}
              {!newMaterial.material_id && (
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {filteredCatalog.length > 0 ? (
                    filteredCatalog.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => selectCatalogItem(item)}
                        className="p-2 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 cursor-pointer flex items-center justify-between transition-colors"
                      >
                        <div>
                          <p className="text-white font-medium text-sm">{item.name}</p>
                          <p className="text-xs text-zinc-400">{item.category || 'Uncategorized'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-orange-400 font-medium text-sm">${item.cost_per_unit}</p>
                          <p className="text-xs text-zinc-500">per {item.unit}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-zinc-500 text-sm">
                      {catalogSearchTerm ? 'No materials found' : 'Start typing to search...'}
                      <Button
                        variant="link"
                        onClick={() => {
                          setAddMode('new');
                          if (catalogSearchTerm) {
                            setNewMaterial({ ...newMaterial, material_name: catalogSearchTerm });
                          }
                        }}
                        className="text-green-400 block mx-auto mt-2"
                      >
                        Create New Material
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
            
            {/* Bundles Tab — one-click add of a saved bundle to this job.
                Bundles ship from estimate-builder, so admins who already
                curated "Driveway Sealcoat Kit" etc. can re-use the exact
                same kit when logging materials on an active job. */}
            <TabsContent value="bundles" className="space-y-3 mt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  value={bundleSearchTerm}
                  onChange={(e) => setBundleSearchTerm(e.target.value)}
                  placeholder="Search bundles..."
                  className="pl-10 bg-zinc-800 border-zinc-700"
                  data-testid="bundles-search-input"
                />
              </div>

              <div className="max-h-72 overflow-y-auto space-y-2" data-testid="bundles-list">
                {materialBundles.length === 0 ? (
                  <div className="text-center py-6 text-zinc-500 text-sm">
                    <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    No material bundles yet.
                    <p className="text-xs mt-1 text-zinc-600">
                      Create bundles from the Material Catalog page to add multiple materials at once.
                    </p>
                  </div>
                ) : (
                  materialBundles
                    .filter((b) => {
                      const q = bundleSearchTerm.trim().toLowerCase();
                      if (!q) return true;
                      return (b.name || '').toLowerCase().includes(q)
                        || (b.description || '').toLowerCase().includes(q);
                    })
                    .map((bundle) => {
                      const itemCount = (bundle.materials || []).length;
                      const isAdding = addingBundleId === bundle.id;
                      return (
                        <div
                          key={bundle.id}
                          className="p-3 rounded-lg bg-zinc-800/60 border border-zinc-700 hover:border-cyan-500/50 transition-colors"
                          data-testid={`bundle-card-${bundle.id}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="text-white font-medium text-sm truncate">{bundle.name}</p>
                              {bundle.description && (
                                <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{bundle.description}</p>
                              )}
                              <p className="text-[11px] text-cyan-400 mt-1">
                                {itemCount} {itemCount === 1 ? 'material' : 'materials'}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              disabled={isAdding || itemCount === 0}
                              onClick={() => addBundleToJob(bundle)}
                              className="bg-cyan-600 hover:bg-cyan-700 text-white flex-shrink-0"
                              data-testid={`bundle-add-btn-${bundle.id}`}
                            >
                              {isAdding ? (
                                <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Adding…</>
                              ) : (
                                <><Plus className="w-3.5 h-3.5 mr-1" /> Add</>
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })
                )}
              </div>
            </TabsContent>
            <TabsContent value="new" className="space-y-4 mt-4">
              <div>
                <Label className="text-zinc-200">Material Name *</Label>
                <Input
                  value={newMaterial.material_name}
                  onChange={(e) => setNewMaterial({ ...newMaterial, material_name: e.target.value })}
                  placeholder="e.g., Premium Epoxy Coating"
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
              
              {/* Auto-add to catalog option */}
              {!isEmployee && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="add_to_catalog"
                      checked={newMaterial.add_to_catalog}
                      onCheckedChange={(checked) => setNewMaterial({ ...newMaterial, add_to_catalog: checked })}
                    />
                    <Label htmlFor="add_to_catalog" className="text-green-400 text-sm font-medium">
                      Save to catalog for future use
                    </Label>
                  </div>
                  
                  {newMaterial.add_to_catalog && (
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div>
                        <Label className="text-zinc-300 text-xs">Category</Label>
                        <Select 
                          value={newMaterial.catalog_category} 
                          onValueChange={(v) => setNewMaterial({ ...newMaterial, catalog_category: v })}
                        >
                          <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-600 h-8 text-sm">
                            <SelectValue placeholder="Category..." />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="Coatings">Coatings</SelectItem>
                            <SelectItem value="Primers">Primers</SelectItem>
                            <SelectItem value="Flooring">Flooring</SelectItem>
                            <SelectItem value="Concrete">Concrete</SelectItem>
                            <SelectItem value="Paint">Paint</SelectItem>
                            <SelectItem value="Landscaping">Landscaping</SelectItem>
                            <SelectItem value="Equipment">Equipment</SelectItem>
                            <SelectItem value="Tools">Tools</SelectItem>
                            <SelectItem value="Supplies">Supplies</SelectItem>
                            <SelectItem value="General">General</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-zinc-300 text-xs">Coverage Rate</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            type="number"
                            min="0"
                            step="0.1"
                            value={newMaterial.coverage_rate || ''}
                            onChange={(e) => setNewMaterial({ ...newMaterial, coverage_rate: parseFloat(e.target.value) || null })}
                            placeholder="0"
                            className="bg-zinc-800 border-zinc-600 h-8 text-sm"
                          />
                          <span className="text-xs text-zinc-500 whitespace-nowrap">sq ft/unit</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          {/* Common fields - always shown */}
          <div className="space-y-4 mt-2">
            <div className={`grid ${isEmployee ? 'grid-cols-2' : 'grid-cols-3'} gap-3`}>
              <div>
                <Label className="text-zinc-200">Quantity</Label>
                <Input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={newMaterial.quantity}
                  onChange={(e) => setNewMaterial({ ...newMaterial, quantity: parseFloat(e.target.value) || 1 })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-zinc-200">Unit</Label>
                <Select value={newMaterial.unit} onValueChange={(v) => setNewMaterial({ ...newMaterial, unit: v })}>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="each">Each</SelectItem>
                    <SelectItem value="sqft">Sq Ft</SelectItem>
                    <SelectItem value="gallon">Gallon</SelectItem>
                    <SelectItem value="bag">Bag</SelectItem>
                    <SelectItem value="box">Box</SelectItem>
                    <SelectItem value="lb">Lb</SelectItem>
                    <SelectItem value="ton">Ton</SelectItem>
                    <SelectItem value="yard">Yard</SelectItem>
                    <SelectItem value="roll">Roll</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {!isEmployee && (
                <div>
                  <Label className="text-zinc-200">Unit Cost</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newMaterial.unit_cost}
                    onChange={(e) => setNewMaterial({ ...newMaterial, unit_cost: parseFloat(e.target.value) || 0 })}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                  />
                </div>
              )}
            </div>

            {!isEmployee && (
              <div>
                <Label className="text-zinc-200">Markup % (Profit)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={newMaterial.markup_percent}
                  onChange={(e) => setNewMaterial({ ...newMaterial, markup_percent: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
                {newMaterial.unit_cost > 0 && newMaterial.quantity > 0 && (
                  <p className="text-xs text-zinc-500 mt-1">
                    Total: ${(newMaterial.quantity * newMaterial.unit_cost * (1 + newMaterial.markup_percent / 100)).toFixed(2)}
                  </p>
                )}
              </div>
            )}

            <div>
              <Label className="text-zinc-200">Reason for Addition</Label>
              <Select value={newMaterial.reason} onValueChange={(v) => setNewMaterial({ ...newMaterial, reason: v })}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Select reason..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="scope_change">Scope Change</SelectItem>
                  <SelectItem value="customer_request">Customer Request</SelectItem>
                  <SelectItem value="unforeseen_condition">Unforeseen Condition</SelectItem>
                  <SelectItem value="material_upgrade">Material Upgrade</SelectItem>
                  <SelectItem value="repair_needed">Repair Needed</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-200">Notes</Label>
              <Textarea
                value={newMaterial.notes}
                onChange={(e) => setNewMaterial({ ...newMaterial, notes: e.target.value })}
                placeholder="Additional details..."
                className="mt-1 bg-zinc-800 border-zinc-700"
                rows={2}
              />
            </div>

            {!isEmployee && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="requires_approval"
                  checked={newMaterial.requires_approval}
                  onCheckedChange={(checked) => setNewMaterial({ ...newMaterial, requires_approval: checked })}
                />
                <Label htmlFor="requires_approval" className="text-zinc-300 text-sm">
                  Requires customer approval (Change Order)
                </Label>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="border-zinc-600">
              Cancel
            </Button>
            <Button 
              onClick={handleAddMaterial} 
              className="bg-orange-500 text-white hover:bg-orange-600"
              disabled={!newMaterial.material_name}
            >
              {addMode === 'new' && newMaterial.add_to_catalog ? 'Add & Save to Catalog' : 'Add Material'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Change Order Dialog */}
      <Dialog open={changeOrderDialogOpen} onOpenChange={setChangeOrderDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-cyan-400" /> Create Change Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-200">Change Order Title *</Label>
              <Input
                value={newChangeOrder.title}
                onChange={(e) => setNewChangeOrder({ ...newChangeOrder, title: e.target.value })}
                placeholder="e.g., Additional Floor Prep Work"
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>

            <div>
              <Label className="text-zinc-200">Description</Label>
              <Textarea
                value={newChangeOrder.description}
                onChange={(e) => setNewChangeOrder({ ...newChangeOrder, description: e.target.value })}
                placeholder="Describe the additional work..."
                className="mt-1 bg-zinc-800 border-zinc-700"
                rows={2}
              />
            </div>

            <div className="p-3 bg-zinc-800 rounded-lg">
              <p className="text-xs text-zinc-400 uppercase mb-2">Materials Included ({selectedForChangeOrder.length})</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {pendingApproval.filter(m => selectedForChangeOrder.includes(m.id)).map((mat) => (
                  <div key={mat.id} className="flex justify-between text-sm">
                    <span className="text-zinc-300">{mat.material_name}</span>
                    <span className="text-orange-400">${mat.total_with_markup?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 pt-2 border-t border-zinc-700 flex justify-between">
                <span className="text-sm text-zinc-400">Materials Total</span>
                <span className="text-orange-400 font-medium">
                  ${pendingApproval
                    .filter(m => selectedForChangeOrder.includes(m.id))
                    .reduce((sum, m) => sum + (m.total_with_markup || 0), 0)
                    .toFixed(2)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-200">Additional Labor Hours</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={newChangeOrder.additional_labor_hours}
                  onChange={(e) => setNewChangeOrder({ ...newChangeOrder, additional_labor_hours: parseFloat(e.target.value) || 0 })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-zinc-200">Hourly Rate</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={newChangeOrder.labor_hourly_rate}
                  onChange={(e) => setNewChangeOrder({ ...newChangeOrder, labor_hourly_rate: parseFloat(e.target.value) || 0 })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>

            {newChangeOrder.additional_labor_hours > 0 && newChangeOrder.labor_hourly_rate > 0 && (
              <p className="text-sm text-zinc-400">
                Labor Total: ${(newChangeOrder.additional_labor_hours * newChangeOrder.labor_hourly_rate).toFixed(2)}
              </p>
            )}

            <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg">
              <div className="flex justify-between">
                <span className="text-primary font-medium">Change Order Total</span>
                <span className="text-primary font-bold">
                  ${(
                    pendingApproval
                      .filter(m => selectedForChangeOrder.includes(m.id))
                      .reduce((sum, m) => sum + (m.total_with_markup || 0), 0) +
                    (newChangeOrder.additional_labor_hours * newChangeOrder.labor_hourly_rate)
                  ).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeOrderDialogOpen(false)} className="border-zinc-600">
              Cancel
            </Button>
            <Button onClick={handleCreateChangeOrder} className="bg-cyan-500 text-white hover:bg-cyan-600">
              <Send className="w-4 h-4 mr-2" /> Create Change Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default JobAdditionalMaterials;
